'use client';

import { memo as Memo, useRef, useLayoutEffect, useMemo, useState, useCallback, Fragment } from 'react';
import { gsap } from 'gsap';

//* Helpers
import breakString from '@helpers/breakString';
import stringToLetters from '@helpers/stringToLetters';

//* Components
import Text from '@/components/global/Text';
import CustomLink from '@components/global/CustomLink';
import RollyJump from '@components/global/SvgComponents/RollyJump';
import ZiggyJump from '@components/global/SvgComponents/ZiggyJump';
import PeezzyJump from '@components/global/SvgComponents/PeezzyJump';
import SharpyJump from '@components/global/SvgComponents/SharpyJump';

//* Styles
import WhatWeDoStyle from './style';
import GameJump from '@/components/global/SvgComponents/GameJump';
import AppJump from '@/components/global/SvgComponents/AppJump';
import SeoJump from '@/components/global/SvgComponents/SeoJump';

const getLetters = (el) => el?.querySelectorAll?.('.letter') ?? [];

const WhatWeDo = Memo(({ active, services, ...data }) => {
	//! States
	const [hoverObj, setHoverObj] = useState(null);

	//! Refs
	const containerRef = useRef(null);
	const ref = useRef([]);
	const topRef1 = useRef();
	const topRef2 = useRef();
	const topRef3 = useRef();

	//! Animation
	const tl = useMemo(() => gsap.timeline({ delay: 0.4 }), []);

	useLayoutEffect(() => {
		if (!topRef1.current || !topRef2.current || !topRef3.current) return;

		const ctx = gsap.context(() => {
			const targets = [...(topRef1.current.children || []), topRef2.current];
			const servicesRefs = [...(topRef3.current.children || [])];
			const italicTarget = topRef1.current.children[1];

			if (active) {
				tl.clear().to(targets, { opacity: 1, duration: 0, stagger: 0.2 }).to(italicTarget, { fontStyle: 'italic', fontWeight: 300, duration: 0, delay: 0.4 }, 0.2);
				tl.to(servicesRefs, { opacity: 1, duration: 0, stagger: 0.15 }, 0.5);
			} else {
				tl.clear().to(targets, { opacity: 0, duration: 0, stagger: 0 }).to(italicTarget, { fontStyle: 'normal', fontWeight: 400, duration: 0 }, 0);
				tl.to(servicesRefs, { opacity: 0, duration: 0, stagger: 0 }, 0);
			}
		}, containerRef);

		return () => ctx.revert();
	}, [active, tl]);

	//! Letter Spacing Animation
	useLayoutEffect(() => {
		const ctx = gsap.context(() => {
			ref.current.forEach((el) => {
				gsap.set(getLetters(el), { letterSpacing: 0 });
			});
		}, containerRef);

		return () => ctx.revert();
	}, [services]);

	const handleHover = useCallback((bool, ind) => {
		setHoverObj(ind);

		const letters = getLetters(ref.current[ind]);
		if (!letters.length) return;

		gsap.to(letters, {
			letterSpacing: bool ? (i) => i + 0.75 : 0,
			duration: 0.3,
		});
	}, []);

	const items = {
		Branding: PeezzyJump,
		Digital: SharpyJump,
		'E-commerce': ZiggyJump,
		Product: RollyJump,
		Game: GameJump,
		App: AppJump,
		Seo: SeoJump,
	};

	const randomMap = useMemo(() => {
		const values = Object.values(items);
		const map = {};

		Object.values(services).forEach((obj) => {
			map[obj.slug] = values[Math.floor(Math.random() * values.length)];
		});

		return map;
	}, [services]);

	const servicesHTML = useMemo(() => {
		return Object.values(services).map((obj, ind) => {
			const Comp = items[obj.type] || randomMap[obj.slug];

			const item = obj;
			const text = item.name;

			return (
				<CustomLink
					key={ind}
					className={'whatWeDoItem'}
					url={`/services/${item.slug}`}>
					<Fragment>
						<div className={'iconCont'}>
							<Comp animate={hoverObj === ind} />
						</div>

						<div
							onMouseEnter={() => handleHover(true, ind)}
							onMouseLeave={() => handleHover(false, ind)}
							ref={(el) => (ref.current[ind] = el)}>
							<Text
								tag={'h3'}
								className={`p-l font-poppins font-regular font-italic description`}>
								{stringToLetters(text)}
							</Text>
						</div>
					</Fragment>
				</CustomLink>
			);
		});
	}, [services, hoverObj, handleHover, randomMap]);

	return (
		<WhatWeDoStyle ref={containerRef}>
			<Text
				tag={'h2'}
				ref={topRef1}
				className={`h2 font-poppins font-semi-bold title`}>
				{breakString(data.title)}
			</Text>

			<div
				className={'descriptionWrap'}
				ref={topRef2}>
				<Text className={`h6 font-poppins font-light description`}>{data.description}</Text>
			</div>

			<div
				className={`whatWeDoWrap`}
				ref={topRef3}>
				{servicesHTML}
			</div>
		</WhatWeDoStyle>
	);
});

export default WhatWeDo;
